import {
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import type { DeviceQueueItem, DeviceQueueType } from '@/services/sysadmin/deviceQueueService';

export type DeviceQueueSortKey =
  | 'type'
  | 'reg'
  | 'makeModel'
  | 'group'
  | 'imei'
  | 'fw'
  | 'lastSeen'
  | 'addedAt'
  | 'status';

export type DeviceQueueSortDir = 'asc' | 'desc';

type Translate = (key: string, options?: { column: string }) => string;

function compareSortValues(
  a: string | number | null,
  b: string | number | null,
  dir: DeviceQueueSortDir,
): number {
  const emptyA = a == null || a === '';
  const emptyB = b == null || b === '';
  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;
  const cmp =
    typeof a === 'number' && typeof b === 'number'
      ? a - b
      : String(a).localeCompare(String(b), 'sv', { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? cmp : -cmp;
}

function sortValue(
  item: DeviceQueueItem,
  key: DeviceQueueSortKey,
  phaseLabel: (type: DeviceQueueType) => string,
): string | number | null {
  switch (key) {
    case 'type':
      return phaseLabel(item.queueType);
    case 'reg':
      return item.registrationNumber;
    case 'makeModel':
      return item.makeModel;
    case 'group':
      return item.groupName;
    case 'imei':
      return item.imei;
    case 'fw':
      return item.currentFirmware;
    case 'lastSeen': {
      const ms = item.lastSeen ? new Date(item.lastSeen).getTime() : NaN;
      return Number.isFinite(ms) ? ms : null;
    }
    case 'addedAt': {
      const ms = item.addedAt ? new Date(item.addedAt).getTime() : NaN;
      return Number.isFinite(ms) ? ms : null;
    }
    case 'status':
      return item.purposeOrStatus;
    default: {
      const _exhaustive: never = key;
      return _exhaustive;
    }
  }
}

export function sortDeviceQueueItems(
  items: DeviceQueueItem[],
  key: DeviceQueueSortKey,
  dir: DeviceQueueSortDir,
  phaseLabel: (type: DeviceQueueType) => string,
): DeviceQueueItem[] {
  return [...items].sort((a, b) =>
    compareSortValues(sortValue(a, key, phaseLabel), sortValue(b, key, phaseLabel), dir),
  );
}

export function nextSortDir(
  currentKey: DeviceQueueSortKey,
  currentDir: DeviceQueueSortDir,
  nextKey: DeviceQueueSortKey,
): DeviceQueueSortDir {
  if (currentKey === nextKey) return currentDir === 'asc' ? 'desc' : 'asc';
  return nextKey === 'lastSeen' || nextKey === 'addedAt' ? 'desc' : 'asc';
}

interface SortHeadersProps {
  t: Translate;
  view: 'queue' | 'done';
  sortKey: DeviceQueueSortKey;
  sortDir: DeviceQueueSortDir;
  onToggle: (key: DeviceQueueSortKey) => void;
}

export function DeviceQueueSortHeaders({ t, view, sortKey, sortDir, onToggle }: SortHeadersProps) {
  const columns: { key: DeviceQueueSortKey; label: string }[] = [
    { key: 'type', label: t('sysAdmin.deviceQueues.colType') },
    { key: 'reg', label: t('sysAdmin.deviceQueues.colReg') },
    { key: 'makeModel', label: t('sysAdmin.deviceQueues.colMakeModel') },
    { key: 'group', label: t('sysAdmin.deviceQueues.colGroup') },
    { key: 'imei', label: t('sysAdmin.deviceQueues.colImei') },
    { key: 'fw', label: t('sysAdmin.deviceQueues.colFw') },
    { key: 'lastSeen', label: t('sysAdmin.deviceQueues.colLastSeen') },
    {
      key: 'addedAt',
      label:
        view === 'done'
          ? t('sysAdmin.deviceQueues.colCompleted')
          : t('sysAdmin.deviceQueues.colAdded'),
    },
    { key: 'status', label: t('sysAdmin.deviceQueues.colStatus') },
  ];

  return (
    <>
      {columns.map(({ key, label }) => {
        const active = sortKey === key;
        const Icon = !active ? ChevronUpDownIcon : sortDir === 'asc' ? ChevronUpIcon : ChevronDownIcon;
        return (
          <th
            key={key}
            className="text-left px-3 py-2 font-medium"
            aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
          >
            <button
              type="button"
              className="inline-flex items-center gap-1 hover:text-textDefault"
              onClick={() => onToggle(key)}
              title={t('sysAdmin.deviceQueues.sortColumn', { column: label })}
            >
              {label}
              <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-primary' : 'opacity-50'}`} aria-hidden />
            </button>
          </th>
        );
      })}
      <th className="text-right px-3 py-2 font-medium">{t('sysAdmin.deviceQueues.colActions')}</th>
    </>
  );
}
